import { Column, PromptCard, Row, type Prompt } from "@1js/pl-card-components";
import { type Group } from "@1js/pl-types";
import * as React from "react";
import { LibraryContext } from "../../pages/prompts/allPromptsLibrary/LibraryContext";
import type { HandlePromptsTabSelected } from "../AllPrompts/AllPrompts.web.data";
import { useStyles } from "../AllPrompts/AllPrompts.web.styles";
import { useYourPrompts } from "./YourPrompts.hook.web";

export type BookmarkPromptCardsProps = {
  prompts: Prompt[];
  resultsAnnouncementId: string;
  promptsToDisplay: Prompt[];
  setLastFocusedPromptId: (promptId: string) => void;
  setCurrentFocusedPromptId: (promptId: string) => void;
  promptRefs: React.MutableRefObject<
    Map<string, React.RefObject<HTMLDivElement>>
  >;
  currentFocusedPromptId: string | null;
  isWorkgroupPublishingEnabled: boolean;
  shareToWorkgroup: (prompts: Prompt[]) => void;
  handleShareViaCopyLinkClicked?: (prompt: Prompt) => Promise<void>;
  handleShareViaEmailClicked?: (prompt: Prompt) => Promise<void>;
  handleCardClick: (prompt: Prompt) => void;
  onPromptBookmarkToggled: (prompt: Prompt) => Promise<void>;
  onDelete?: (prompt: Prompt) => void;
  groups?: Group[];
  handlePromptsTabSelected: HandlePromptsTabSelected;
  onBookmarkToggled: (() => void) | undefined;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const BookmarkPromptCards: React.FC<BookmarkPromptCardsProps> = ({
  prompts,
  resultsAnnouncementId,
  promptsToDisplay,
  setLastFocusedPromptId,
  setCurrentFocusedPromptId,
  promptRefs,
  currentFocusedPromptId,
  isWorkgroupPublishingEnabled,
  shareToWorkgroup,
  handleShareViaCopyLinkClicked,
  handleShareViaEmailClicked,
  handleCardClick,
  onPromptBookmarkToggled,
  onDelete,
  groups,
  handlePromptsTabSelected,
  onBookmarkToggled,
  setDialogOpen,
}) => {
  const styles = useStyles();

  const { isFlightActive, chooseBestProductForHandoff } = useYourPrompts({
    handlePromptsTabSelected,
    onBookmarkToggled,
    onDelete,
    setDialogOpen,
  });

  const appearance = isFlightActive("isGoBoldPromptCardEnabled")
    ? "web-v2"
    : "web";

  const cardColStyles = isFlightActive("isGoBoldPromptCardEnabled")
    ? styles.cardColV2
    : styles.cardCol;

  const { state } = React.useContext(LibraryContext);

  return (
    <Row
      role="list"
      aria-label="Prompt"
      aria-describedby={resultsAnnouncementId}
      className={styles.cards}
      data-testid="prompt-cards"
    >
      {promptsToDisplay.map((prompt: Prompt, idx: number) => {
        if (!promptRefs.current.has(prompt.Id)) {
          promptRefs.current.set(prompt.Id, React.createRef());
        }
        const handleFocus = () => {
          setLastFocusedPromptId(prompt.Id);
          setCurrentFocusedPromptId(prompt.Id);
        };
        return (
          <Column key={prompt.Id} size={3} className={cardColStyles}>
            <PromptCard
              appearance={appearance}
              key={prompt.Id}
              displayPosition={idx}
              isPromptFocused={currentFocusedPromptId === prompt.Id}
              onFocus={handleFocus}
              prompt={prompt}
              isFavoriteEnabled={isFlightActive("isBookmarksEnabled")}
              isAgentPromptFavoriteEnabled={isFlightActive(
                "isAgentPromptFavoriteEnabled"
              )}
              isPromptSavingEnabled={isFlightActive("isPromptSavingEnabled")}
              isPromptSharingEnabled={isFlightActive("isPromptSharingEnabled")}
              isPromptSharingViaEmailEnabled={isFlightActive(
                "isPromptSharingViaEmailEnabled"
              )}
              isPromptSharingToWorkgroupEnabled={
                isWorkgroupPublishingEnabled &&
                (isFlightActive(
                  "isWorkgroupPublishingMicrosoftPromptsEnabled"
                ) ||
                  prompt.Origin === "User")
              }
              onShareToWorkgroupClicked={shareToWorkgroup}
              onShareViaCopyLinkClicked={handleShareViaCopyLinkClicked}
              onShareViaEmailClicked={handleShareViaEmailClicked}
              onClicked={handleCardClick}
              onFavoriteToggled={onPromptBookmarkToggled}
              isBadgesEnabled={true}
              isPromptLikingEnabled={isFlightActive("isPromptLikingEnabled")}
              ref={promptRefs.current.get(prompt.Id)}
              onDelete={onDelete}
              aria-posinset={idx + 1}
              aria-setsize={prompts.length}
              groups={groups}
              handoffProduct={chooseBestProductForHandoff(prompt)}
              extensionIconMap={
                isFlightActive("isAgentPromptSavingEnabled") ||
                isFlightActive("isAgentPromptFavoriteEnabled")
                  ? state.extensionIconMap
                  : undefined
              }
            />
          </Column>
        );
      })}
    </Row>
  );
};
